/**
 * LPA Wealth — Parser de Extratos
 * Suporta:
 *   • XP Investimentos — XLSX (Posição Detalhada)
 *   • BTG Pactual      — XLSX (CDBs / Custódia)
 *   • PDF genérico     — qualquer banco (Bradesco, Itaú, Santander, Inter…)
 *     via extração de texto + padrões regex BRL (pdfjs bundled)
 */
// XLSX é carregado sob demanda (lazy) em parseExtrato() para não aumentar
// o bundle inicial (~500 KB brutos / ~130 KB gzip).
// NÃO restaure o import estático aqui em cima.

// PDF.js — carregado SOB DEMANDA (lazy) via dynamic import em loadPDFJS().
// Antes era import estático, mas isso arrastava ~400KB de pdfjs-dist pro
// bundle de entrada mesmo pra usuários que nunca importam extrato.
// NÃO restaure o import estático aqui — quebra o code-splitting.

// ─── Utilitários ──────────────────────────────────────────────────

const toNum = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return v;
  const s = String(v)
    .replace(/[R$\s%]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
};

const normStr = (s) =>
  String(s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

// Remove caracteres que causam "unsupported Unicode escape sequence" no JSON/Supabase
/* eslint-disable no-control-regex */
const _CTRL_RE = new RegExp("[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]", "g");
const _SURROGATE_RE = /[\uD800-\uDFFF]/g;
/* eslint-enable no-control-regex */
const sanitizeStr = (s) => {
  if (typeof s !== "string") return s;
  return s
    .replace(_CTRL_RE, "") // control chars
    .replace(_SURROGATE_RE, "") // lone surrogates
    .replace(/\\/g, "") // bare backslashes (JSON-unsafe)
    .trim();
};

const colIdx = (headers, aliases) => {
  const ns = headers.map(normStr);
  for (const a of aliases) {
    const idx = ns.findIndex((h) => h.includes(normStr(a)));
    if (idx !== -1) return idx;
  }
  return -1;
};

// ─── Detecta corretora pelo nome do arquivo ───────────────────────

export function detectBrokerFromFilename(filename) {
  const f = normStr(filename);
  if (f.includes("avenue") || f.includes("stmt_")) return "avenue";
  if (f.includes("xp")) return "xp";
  if (f.includes("btg")) return "btg";
  if (f.includes("bradesco")) return "bradesco";
  if (f.includes("itau") || f.includes("itaú")) return "itau";
  if (f.includes("santander")) return "santander";
  // "Relatório de Rentabilidade" (com ou sem hash no nome) → Interwin
  if (f.includes("rentabilidade")) return "interwin";
  if (f.includes("relatorio") && f.includes("inter")) return "interwin";
  if (f.includes("inter")) return "inter";
  if (f.includes("nubank") || f.includes("nu")) return "nubank";
  if (f.includes("safra")) return "safra";
  if (f.includes("modal")) return "modal";
  return "unknown";
}

// ─── PDF: PDF.js lazy-loaded (pdfjs-dist) ────────────────────────

let _pdfjsCache = null;
async function loadPDFJS() {
  if (_pdfjsCache) return _pdfjsCache;
  const pdfjsLib = await import("pdfjs-dist");
  const workerMod = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
  pdfjsLib.GlobalWorkerOptions.workerSrc = workerMod.default;
  _pdfjsCache = pdfjsLib;
  return pdfjsLib;
}

// ── OCR fallback via Tesseract.js (lazy-loaded) ────────────────
// Alguns relatórios (ex: Inter Win "Relatório Consolidado") são
// gerados com texto vetorizado como paths, sem text layer.
// Nesse caso, renderizamos as páginas em canvas e fazemos OCR.
//
// IMPORTANTE — Tesseract.js carrega 3 recursos em runtime:
//   1) worker.min.js  (Web Worker script)
//   2) tesseract-core WASM  (engine de OCR compilada)
//   3) eng.traineddata  (modelo de linguagem ~4 MB)
// Todos esses são servidos de public/tesseract/ para evitar
// dependência de CDN (jsdelivr) que falha por CSP/rede em produção.
async function ocrPDFPages(pdf, maxPages = 6) {
  const Tesseract = await import("tesseract.js");
  const { createWorker } = Tesseract;

  // Paths locais — servidos do public/tesseract/ via Vite.
  // window.location.origin garante URL absoluto que funciona
  // dentro do Blob URL Web Worker (importScripts precisa de abs URL).
  const base = typeof window !== "undefined" ? window.location.origin : "";

  // Tenta usar eng+por (Português) se disponível; fallback para eng
  let worker;
  try {
    worker = await createWorker("eng+por", 1, {
      workerPath: `${base}/tesseract/worker.min.js`,
      corePath: `${base}/tesseract/`,
      langPath: `${base}/tesseract`,
      logger: (m) => console.info("[OCR]", m.status, Math.round((m.progress || 0) * 100) + "%"),
    });
  } catch (_) {
    console.info("[OCR] por.traineddata não disponível — usando apenas eng");
    worker = await createWorker("eng", 1, {
      workerPath: `${base}/tesseract/worker.min.js`,
      corePath: `${base}/tesseract/`,
      langPath: `${base}/tesseract`,
      logger: (m) => console.info("[OCR]", m.status, Math.round((m.progress || 0) * 100) + "%"),
    });
  }

  const pagesToOCR = Math.min(pdf.numPages, maxPages);
  let fullText = "";

  for (let i = 1; i <= pagesToOCR; i++) {
    const page = await pdf.getPage(i);
    // Escala 2x para melhor qualidade de OCR
    const vp = page.getViewport({ scale: 2.0 });

    // Usa canvas DOM (mais compatível que OffscreenCanvas)
    const canvas = document.createElement("canvas");
    canvas.width = vp.width;
    canvas.height = vp.height;
    const ctx = canvas.getContext("2d");
    await page.render({ canvasContext: ctx, viewport: vp }).promise;

    // Converte para blob — tenta toBlob, fallback para toDataURL
    let imgData;
    try {
      imgData = await new Promise((res, rej) => {
        canvas.toBlob((b) => (b ? res(b) : rej(new Error("toBlob null"))), "image/png");
      });
    } catch (_) {
      imgData = canvas.toDataURL("image/png");
    }

    const {
      data: { text },
    } = await worker.recognize(imgData);
    fullText += `--- Página ${i} ---\n${text}\n`;
  }

  await worker.terminate();
  return fullText;
}

async function extractPDFText(file) {
  const lib = await loadPDFJS();
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await lib.getDocument({ data: arrayBuffer }).promise;
  let fullText = "";
  let totalItems = 0;

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const vp = page.getViewport({ scale: 1.0 });
    const content = await page.getTextContent();

    // Mapeia itens com coordenadas visuais
    const items = content.items
      .filter((item) => item.str && item.str.trim())
      .map((item) => ({
        str: item.str,
        x: item.transform[4],
        // PDF: y começa de baixo; inverte para leitura de cima para baixo
        y: vp.height - item.transform[5],
        h: item.height || Math.abs(item.transform[3]) || Math.abs(item.transform[0]) || 8,
      }));

    totalItems += items.length;
    if (!items.length) continue;

    // Ordena: de cima para baixo (y), depois esquerda para direita (x) na mesma linha
    items.sort((a, b) => {
      const tol = Math.max(a.h, b.h, 4) * 0.55;
      const dy = a.y - b.y;
      return Math.abs(dy) > tol ? dy : a.x - b.x;
    });

    // Agrupa em linhas visuais por proximidade de y
    const lines = [];
    let curLine = [items[0]];
    for (let j = 1; j < items.length; j++) {
      const cur = items[j];
      const tol = Math.max(curLine[0].h, cur.h, 4) * 0.55;
      if (Math.abs(cur.y - curLine[0].y) <= tol) {
        curLine.push(cur);
      } else {
        lines.push(curLine);
        curLine = [cur];
      }
    }
    if (curLine.length) lines.push(curLine);

    // Cada linha visual → uma linha de texto
    const pageText = lines.map((ln) => ln.map((it) => it.str).join(" ")).join("\n");
    fullText += `--- Página ${i} ---\n${pageText}\n`;
  }

  // ── OCR fallback: PDF sem text layer (texto vetorizado como paths) ──
  // Se pdf.js extraiu menos de 20 itens de texto de todas as páginas,
  // o PDF provavelmente é image-based ou tem texto como vector paths.
  // Renderiza para canvas e faz OCR via Tesseract.js.
  if (totalItems < 20 && pdf.numPages >= 2) {
    console.info(
      `[extratoParser] PDF sem text layer (${totalItems} itens em ${pdf.numPages} págs). Iniciando OCR…`
    );
    // NÃO silencia o erro — se OCR falhar, parseExtrato deve saber
    const ocrText = await ocrPDFPages(pdf);
    if (ocrText.replace(/[\s\n-]/g, "").length > fullText.replace(/[\s\n-]/g, "").length) {
      return ocrText;
    }
  }

  return fullText;
}

// ─── Avenue Securities — Parser de Statement PDF ─────────────────
// Formato: "Stmt_YYYYMMDD.pdf" da corretora americana Avenue
// Extrai: total portfolio USD, posições (ticker, shares, preço, valor)
//
// Layout real da tabela (pg.2):
//   "[Nome do ativo...] TICKER [CUSIP] C|M  QTY  PRICE  $?VALUE  $?LASTVAL  CHANGE%  PORTFOLIO%"
//   • $? — o símbolo "$" é opcional (valores menores podem não tê-lo na extração)
//   • CHANGE% — o "%" pode estar ausente em certos valores (ex: "-7.55 1.345%")
//   • TOTAL PRICED PORTFOLIO $X,XXX.XX — linha do total real (pg.2)

function parseAvenuePDF(rawText) {
  const t = rawText;

  // Detecta se realmente é extrato Avenue
  const isAvenue =
    /avenue|stmt_\d{8}/i.test(t) ||
    /account\s+number|account\s+value|equity\s+value/i.test(t) ||
    /opening\s+(?:balance|value)|closing\s+(?:balance|value)/i.test(t) ||
    /total\s+priced\s+portfolio/i.test(t) ||
    /apex\s+clearing/i.test(t);
  if (!isAvenue) return null;

  const parseUSD = (s) => {
    if (!s) return null;
    const n = parseFloat(String(s).replace(/[,$\s]/g, ""));
    return isNaN(n) ? null : n;
  };

  // ── Total portfolio (closing value) ───────────────────────────────
  // Prioritidade: "TOTAL PRICED PORTFOLIO $ 9,561.08" (pg.2)
  let total_usd = null;
  const totalPatterns = [
    // TOTAL PRICED PORTFOLIO (mais confiável — sempre é o fechamento do mês)
    /total\s+priced\s+portfolio\s*\$\s*([\d,]+\.?\d*)/i,
    /closing\s+(?:portfolio\s+)?(?:balance|value)\s*\$?\s*([\d,]+\.?\d*)/i,
    /total\s+portfolio\s*[:\s]\s*\$?\s*([\d,]+\.?\d*)/i,
    /total\s+(?:portfolio\s+)?value\s*\$?\s*([\d,]+\.?\d*)/i,
    // "Total Equity Holdings $ X $ Y" — captura o SEGUNDO valor (fechamento)
    /total\s+equity\s+holdings?\s+\$?\s*[\d,]+\.?\d*\s+\$?\s*([\d,]+\.?\d*)/i,
    /account\s+(?:total\s+)?value\s*\$?\s*([\d,]+\.?\d*)/i,
    /portfolio\s+total\s*\$?\s*([\d,]+\.?\d*)/i,
    /\$\s*([\d,]+\.\d{2})\s*(?:total|portfolio|account)/i,
  ];
  for (const re of totalPatterns) {
    const m = t.match(re);
    if (m) {
      const v = parseUSD(m[1]);
      if (v && v > 10) {
        total_usd = v;
        break;
      }
    }
  }
  // Fallback: maior valor em USD com formato $X,XXX.XX
  if (!total_usd) {
    const allVals = [...t.matchAll(/\$\s*([\d]{1,3}(?:,\d{3})*\.\d{2})/g)]
      .map((m) => parseUSD(m[1]))
      .filter((v) => v && v > 100)
      .sort((a, b) => b - a);
    if (allVals.length) total_usd = allVals[0];
  }

  // ── Posições individuais ───────────────────────────────────────────
  // Formato real da tabela:
  //   "[desc...] TICKER [CUSIP] C|M  QTY  PRICE  $?VALUE  $?LASTVAL  CHANGE%?"
  //   • $? → "$" é opcional (PDF.js pode omitir em valores pequenos)
  //   • %? → "%" após o valor de change é opcional
  const positions = [];
  const SKIP_TICKERS = new Set([
    "PAGE",
    "OF",
    "EST",
    "YTD",
    "NET",
    "USD",
    "ANNUAL",
    "INCOME",
    "TOTAL",
    "CASH",
    "APEX",
    "VELOCITY",
    "FINRA",
    "SIPC",
    "SEC",
    "ETF",
    "ADR",
    "LP",
    "NA",
  ]);

  const posRe =
    /\b([A-Z]{2,6})\b\s+(?:[A-Z0-9]{6,}\s+)?(?:C|M)\s+([\d]+\.[\d]+)\s+([\d,]+\.?[\d]*)\s+\$?\s*([\d,]+\.[\d]{2})\s+\$?\s*([\d,]+\.[\d]{2})\s+([+-]?[\d]+\.?[\d]*)%?/g;
  let pm;
  while ((pm = posRe.exec(t)) !== null) {
    const ticker = pm[1];
    if (SKIP_TICKERS.has(ticker)) continue;

    const qty = parseFloat(pm[2]);
    const price = parseUSD(pm[3]);
    const value = parseUSD(pm[4]);
    const chg = parseFloat(pm[6]);

    if (!value || value <= 0 || qty <= 0) continue;

    // Extrai nome: texto antes do ticker na mesma linha
    const lineStart = t.lastIndexOf("\n", pm.index) + 1;
    const namePart = t.slice(lineStart, pm.index).replace(/^\s+|\s+$/, "");
    const name = sanitizeStr(namePart.length > 1 ? namePart.slice(-80) : ticker);

    positions.push({
      ticker,
      name,
      shares: qty,
      price: price || 0,
      value,
      chg1m: isNaN(chg) ? null : chg,
    });
  }

  // ── Cash / saldo disponível ────────────────────────────────────────
  let cash_usd = null;
  const cashRe =
    /(?:total\s+cash(?:\s+&\s+cash\s+equivalents)?|cash\s+balance|money\s+market)\s*\$?\s*([\d,]+\.?\d*)/i;
  const cm = t.match(cashRe);
  if (cm) cash_usd = parseUSD(cm[1]);

  // ── Retorno do mês (se disponível) ────────────────────────────────
  let ret_month_usd_pct = null;
  const retRe =
    /(?:return|performance|change)\s+(?:for\s+(?:the\s+)?month|this\s+month|mtd)[^\n]{0,60}([+-]?[\d.]+)%/i;
  const rm = t.match(retRe);
  if (rm) ret_month_usd_pct = parseFloat(rm[1]);

  // ── Conta ──────────────────────────────────────────────────────────
  let account = null;
  const acRe = /account\s*(?:number|#|no\.?)[:\s]*([\d-]+)/i;
  const acM = t.match(acRe);
  if (acM) account = acM[1].trim();

  // ── Período ────────────────────────────────────────────────────────
  // Converte "January 31, 2026" → "Jan/26" (mesmo formato que o parser Interwin)
  let period = null;
  const periodRe = /([A-Za-z]+ \d{1,2}, \d{4})\s*[-–]\s*([A-Za-z]+ \d{1,2}, \d{4})/;
  const periodM = t.match(periodRe);
  if (periodM) {
    const EN_MONTH_IDX = {
      january: 0,
      february: 1,
      march: 2,
      april: 3,
      may: 4,
      june: 5,
      july: 6,
      august: 7,
      september: 8,
      october: 9,
      november: 10,
      december: 11,
    };
    const MONTHS_PT_ABBR = [
      "Jan",
      "Fev",
      "Mar",
      "Abr",
      "Mai",
      "Jun",
      "Jul",
      "Ago",
      "Set",
      "Out",
      "Nov",
      "Dez",
    ];
    // Usa a data final do período para determinar o mês/ano de referência
    const endParts = periodM[2].match(/^([A-Za-z]+)\s+\d+,\s+(\d{4})$/);
    if (endParts) {
      const mIdx = EN_MONTH_IDX[endParts[1].toLowerCase()];
      if (mIdx !== undefined) {
        period = `${MONTHS_PT_ABBR[mIdx]}/${endParts[2].slice(2)}`;
      }
    }
    if (!period) period = periodM[0]; // fallback ao raw se parsing falhar
  }

  return {
    source: "avenue_pdf",
    currency: "USD",
    account,
    period,
    patrimonio_total: null, // Avenue é USD — não converte automaticamente
    patrimonio_usd: total_usd,
    cash_usd,
    ret_month: ret_month_usd_pct,
    positions,
    warnings: total_usd ? [] : ["Total do portfólio Avenue não identificado — verifique o PDF."],
  };
}

// ─── Avenue Securities — Parser de Position Export CSV ───────────
// Formato: CSV exportado pela plataforma web da Avenue Securities
// Colunas típicas: Symbol, Description, Quantity, Last Price,
//                  Market Value, Cost Basis, Unrealized Gain/Loss,
//                  Unrealized Gain/Loss %, Day Change, Day Change %
// Também aceita formatos com: Ticker, Shares, Price, Value

function parseAvenueCSV(text) {
  if (!text) return null;

  // Detecta se é realmente extrato Avenue
  const tn = text.toLowerCase();
  const isAvenue =
    tn.includes("avenue") ||
    tn.includes("market value") ||
    tn.includes("unrealized") ||
    tn.includes("cost basis") ||
    /\b(aapl|msft|amzn|googl|tsla|spx|voo|qqq|urnm)\b/i.test(text);
  if (!isAvenue) return null;

  const parseUSD = (s) => {
    if (s === undefined || s === null || s === "" || s === "-" || s === "N/A") return null;
    const n = parseFloat(
      String(s)
        .replace(/[$,\s()]/g, "")
        .replace(/\((.+)\)/, "-$1")
    );
    return isNaN(n) ? null : n;
  };

  // Separa linhas e detecta delimitador
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const delim = text.includes("\t") ? "\t" : ",";

  // Encontra linha de cabeçalho
  let headerIdx = -1;
  let headers = [];
  for (let i = 0; i < Math.min(lines.length, 10); i++) {
    const cols = lines[i].split(delim).map((c) => c.replace(/^["']|["']$/g, "").trim());
    const norm = cols.map((c) => c.toLowerCase());
    if (
      norm.some((h) => h.includes("symbol") || h.includes("ticker")) &&
      norm.some(
        (h) =>
          h.includes("value") ||
          h.includes("market") ||
          h.includes("quantity") ||
          h.includes("shares")
      )
    ) {
      headerIdx = i;
      headers = norm;
      break;
    }
  }
  if (headerIdx === -1) return null; // não reconheceu o formato

  // Índices das colunas relevantes
  const iSym = headers.findIndex((h) => h.includes("symbol") || h === "ticker");
  const iDesc = headers.findIndex(
    (h) => h.includes("description") || h.includes("name") || h.includes("security")
  );
  const iQty = headers.findIndex(
    (h) => h.includes("quantity") || h.includes("shares") || h.includes("qty")
  );
  const iPrice = headers.findIndex((h) => h === "last price" || h === "price" || h === "last");
  const iMV = headers.findIndex(
    (h) => h.includes("market value") || (h.includes("value") && !h.includes("unrealized"))
  );
  const iCost = headers.findIndex((h) => h.includes("cost basis") || h.includes("cost"));
  const iUGL = headers.findIndex((h) => h.includes("unrealized gain") && !h.includes("%"));
  const iChg = headers.findIndex(
    (h) => (h.includes("day change") || h.includes("change")) && h.includes("%")
  );

  if (iMV === -1 && iSym === -1) return null;

  // Parse das posições
  const positions = [];
  let cash_usd = null;
  let total_usd = null;

  for (let i = headerIdx + 1; i < lines.length; i++) {
    const raw = lines[i];
    if (!raw || raw.startsWith("#") || raw.startsWith("//")) continue;

    // Divide respeitando aspas
    const cols = raw.split(delim).map((c) => c.replace(/^["']|["']$/g, "").trim());
    const sym = iSym >= 0 ? cols[iSym] : null;
    const desc = iDesc >= 0 ? cols[iDesc] : "";
    const mv = iMV >= 0 ? parseUSD(cols[iMV]) : null;

    // Linha de total/portfolio
    const rowLabel = (sym || desc || "").toLowerCase();
    if (
      rowLabel.includes("total") ||
      rowLabel.includes("portfolio total") ||
      rowLabel.includes("account total")
    ) {
      if (mv && mv > 0) total_usd = mv;
      continue;
    }
    // Linha de cash/sweep
    if (
      rowLabel.includes("cash") ||
      rowLabel.includes("sweep") ||
      rowLabel.includes("money market")
    ) {
      if (mv && mv > 0) cash_usd = mv;
      continue;
    }

    if (!sym || sym.length > 8 || !/^[A-Z]/.test(sym)) continue;

    const qty = iQty >= 0 ? parseUSD(cols[iQty]) : null;
    const price = iPrice >= 0 ? parseUSD(cols[iPrice]) : null;
    const cost = iCost >= 0 ? parseUSD(cols[iCost]) : null;
    const ugl = iUGL >= 0 ? parseUSD(cols[iUGL]) : null;
    const chgPct = iChg >= 0 ? parseUSD(cols[iChg]) : null;

    if (mv && mv > 0) {
      positions.push({
        ticker: sym,
        name: desc || sym,
        shares: qty || (mv && price ? +(mv / price).toFixed(4) : 0),
        price: price || 0,
        value: mv,
        cost: cost || null,
        ugl: ugl || null,
        chg1d: chgPct || null,
      });
    }
  }

  // Calcula total se não encontrado explicitamente
  if (!total_usd && positions.length > 0) {
    total_usd = positions.reduce((s, p) => s + (p.value || 0), 0) + (cash_usd || 0);
  }

  const warnings = [];
  if (!total_usd)
    warnings.push("Total do portfólio Avenue (CSV) não identificado — verifique o arquivo.");
  if (!positions.length) warnings.push("Nenhuma posição encontrada no CSV da Avenue.");

  return {
    source: "avenue_csv",
    currency: "USD",
    account: null,
    patrimonio_total: null, // USD — não converte automaticamente
    patrimonio_usd: total_usd,
    cash_usd,
    ret_month: null, // CSV não contém retorno mensal
    positions,
    warnings,
  };
}

// ─── Interwin / Inter — Parser de Relatório de Rentabilidade PDF ──
// Formato: relatório mensal enviado pelo Banco Inter / Interwin
// Extrai: patrimônio, fluxos, rentabilidades, benchmarks, períodos,
//         posições detalhadas (tabela completa), liquidez, movimentações
//
// IMPORTANTE — quirks do PDF.js com este relatório:
//  • Labels e valores ficam em linhas SEPARADAS (layout multi-coluna pg.2)
//  • Tabela de risco/retorno está em formato de LINHAS ("03 meses 11,41 …")
//  • Colunas de % dos ativos NÃO têm símbolo "%" no PDF
//  • Sinal negativo "-" vira um elemento PDF separado → PDF.js o junta como
//    ESPAÇO duplo antes do número (ex: "  4,12" = −4,12%)

function parseInterwinPDF(rawText) {
  const t = rawText;
  const tn = normStr(rawText);

  const isInterwin =
    tn.includes("interwin") ||
    tn.includes("banco inter") ||
    tn.includes("relatorio de rentabilidade") ||
    tn.includes("relatorio consolidado") ||
    tn.includes("patrimonio em") ||
    tn.includes("patrimenio em"); // OCR misreads ô→é
  if (!isInterwin) return null;

  // ── Helpers ───────────────────────────────────────────────────────
  // BRL positivo (patrimônio, valores de saldo)
  const parseBRL = (s) => {
    if (!s) return null;
    const n = parseFloat(
      String(s)
        .replace(/[R$\s.]/g, "")
        .replace(",", ".")
    );
    return isNaN(n) || n <= 0 ? null : n;
  };
  // Número bruto — preserva zero e negativos (fluxos, percentuais com sinal)
  const rawNum = (s) => {
    if (s === null || s === undefined || s === "") return null;
    const n = parseFloat(
      String(s)
        .replace(/[R$\s.]/g, "")
        .replace(",", ".")
    );
    return isNaN(n) ? null : n;
  };
  const parsePct = (s) => {
    if (!s) return null;
    const n = parseFloat(String(s).replace(",", ".").replace(/[%\s]/g, ""));
    return isNaN(n) ? null : n;
  };

  const MONTHS_PT = [
    "jan",
    "fev",
    "mar",
    "abr",
    "mai",
    "jun",
    "jul",
    "ago",
    "set",
    "out",
    "nov",
    "dez",
  ];

  // ── 1. Ref date & patrimônio ───────────────────────────────────
  let patrimonio_total = null;
  let ref_date = null;
  let period = null;

  // OCR pode ler "Patrimônio" como "Patriménio" (ô→é) — aceita ambos
  const dateMRe = /patrim[oôeéê]nio\s+em\s+(\d{1,2})\/(\d{1,2})\/(\d{4})/i;
  const dateM = t.match(dateMRe);
  if (dateM) {
    const [, dd, mm, yyyy] = dateM;
    ref_date = `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
    const mIdx = parseInt(mm, 10) - 1;
    period = `${MONTHS_PT[mIdx].charAt(0).toUpperCase()}${MONTHS_PT[mIdx].slice(1)}/${yyyy.slice(2)}`;
    const afterDate = t.slice(dateM.index + dateM[0].length);
    const brlAfter = afterDate.match(/([\d]{1,3}(?:\.[\d]{3})+,\d{2})/);
    if (brlAfter) {
      const v = parseBRL(brlAfter[1]);
      if (v && v > 10000) patrimonio_total = v;
    }
  }
  // Fallback: qualquer data dd/mm/yyyy no texto (ex: "31/01/2026")
  if (!ref_date) {
    const anyDateRe = /\b(\d{2})\/(\d{2})\/(\d{4})\b/g;
    let adm;
    while ((adm = anyDateRe.exec(t)) !== null) {
      const [, dd, mm, yyyy] = adm;
      const m = parseInt(mm, 10),
        y = parseInt(yyyy, 10),
        d = parseInt(dd, 10);
      if (m >= 1 && m <= 12 && d >= 1 && d <= 31 && y >= 2020 && y <= 2030) {
        ref_date = `${yyyy}-${mm}-${dd}`;
        const mIdx = m - 1;
        period = `${MONTHS_PT[mIdx].charAt(0).toUpperCase()}${MONTHS_PT[mIdx].slice(1)}/${yyyy.slice(2)}`;
        break; // usa a primeira data encontrada
      }
    }
  }

  // ── 2. Bloco resumo pg.2 ──────────────────────────────────────
  // 1ª linha valores: "R$ 1.125.926,95 R$ 0,00 R$ 61.706,81 R$ 2.670,77"
  // OCR pode ler "R$" como "RS" — R[S$] aceita ambos
  let aplicacoes_mes = null,
    resgates_mes = null,
    eventos_mes = null;
  const sum4Re =
    /R[S$]\s*([\d.]+,\d{2})\s+R[S$]\s*([\d.]+,\d{2})\s+R[S$]\s*([\d.]+,\d{2})\s+R[S$]\s*([\d.]+,\d{2})/;
  const sum4M = t.match(sum4Re);
  if (sum4M) {
    if (!patrimonio_total) patrimonio_total = parseBRL(sum4M[1]);
    aplicacoes_mes = rawNum(sum4M[2]);
    resgates_mes = parseBRL(sum4M[3]);
    eventos_mes = rawNum(sum4M[4]);
  }

  // 2ª linha valores: "R$ 65.401,09 5,95% 5,95% 17,41%"
  // \s* antes de % aceita espaço opcional entre o número e o símbolo
  let ganhos_mes = null,
    ret_month = null,
    ret_year = null,
    ret_inception = null;
  const sum2Re =
    /R[S$]\s*([\d.]+,\d{2})\s+([+-]?[\d]+,\d{2})\s*%\s+([+-]?[\d]+,\d{2})\s*%\s+([+-]?[\d]+,\d{2})\s*%/;
  const sum2M = t.match(sum2Re);
  if (sum2M) {
    ganhos_mes = parseBRL(sum2M[1]);
    ret_month = parsePct(sum2M[2]);
    ret_year = parsePct(sum2M[3]);
    ret_inception = parsePct(sum2M[4]);
  }
  // Fallback ret_month: procura logo após a linha de ganho financeiro (caso esteja em linhas separadas)
  if (!ret_month && sum4M) {
    const afterGanhos = t.slice(sum4M.index + sum4M[0].length, sum4M.index + sum4M[0].length + 300);
    const pctM = afterGanhos.match(/([+-]?\d{1,3},\d{2})\s*%/);
    if (pctM) {
      const v = parsePct(pctM[1]);
      if (v !== null && v > -30 && v < 30) ret_month = v;
    }
  }
  // Fallback ret_month: "No Mês" ou "rentabilidade" + percentual próximo
  if (!ret_month) {
    const retMesRe =
      /(?:no\s+m[eê]s|rentabilidade\s+m(?:ensal|[eê]s))[^\n]{0,120}?([+-]?\d{1,3},\d{2})\s*%/i;
    const retMesM = t.match(retMesRe);
    if (retMesM) {
      const v = parsePct(retMesM[1]);
      if (v !== null && v > -30 && v < 30) ret_month = v;
    }
  }

  // Fallbacks individuais para fluxos
  if (aplicacoes_mes === null) {
    const aplM = t.match(/aplica[cç][oõ]es?\s+(?:no\s+)?m[eê]s[^\n]{0,50}([\d.]+,\d{2})/i);
    if (aplM) aplicacoes_mes = rawNum(aplM[1]);
  }
  if (resgates_mes === null) {
    const resM = t.match(/resgates?\s+(?:no\s+)?m[eê]s[^\n]{0,50}([\d.]+,\d{2})/i);
    if (resM) resgates_mes = parseBRL(resM[1]);
  }

  // ── 3. Tabela Ganhos Financeiros — linha TOTAL ─────────────────
  // "TOTAL 65.401,09 65.401,09 123.347,56 186.345,11 194.065,72 227.821,03"
  // Cols: No Mês | No Ano | 03 meses | 06 meses | 12 meses | Desde
  let ganho_12m = null;
  const tgRe =
    /TOTAL\s+([\d.]+,\d{2})\s+([\d.]+,\d{2})\s+([\d.]+,\d{2})\s+([\d.]+,\d{2})\s+([\d.]+,\d{2})\s+([\d.]+,\d{2})/;
  const tgM = t.match(tgRe);
  if (tgM) {
    if (!ganhos_mes) ganhos_mes = parseBRL(tgM[1]);
    ganho_12m = parseBRL(tgM[5]);
  }

  // ── 4. Tabela de períodos (formato LINHA) ──────────────────────
  // "03 meses  11,41  3,48  328,19  10,44"
  // Cols: Carteira% | CDI% | % do CDI | Volatilidade
  const periods = [];
  const PERIOD_DEFS = [
    {
      label: "03m",
      re: /0?3\s*m[eê]s(?:es)?\s+([+-]?[\d]+,\d{2})\s+([+-]?[\d]+,\d{2})\s+([+-]?[\d]+,\d{2})\s+([+-]?[\d]+,\d{2})/i,
    },
    {
      label: "06m",
      re: /0?6\s*m[eê]s(?:es)?\s+([+-]?[\d]+,\d{2})\s+([+-]?[\d]+,\d{2})\s+([+-]?[\d]+,\d{2})\s+([+-]?[\d]+,\d{2})/i,
    },
    {
      label: "12m",
      re: /12\s*m[eê]s(?:es)?\s+([+-]?[\d]+,\d{2})\s+([+-]?[\d]+,\d{2})\s+([+-]?[\d]+,\d{2})\s+([+-]?[\d]+,\d{2})/i,
    },
    {
      label: "24m",
      re: /24\s*m[eê]s(?:es)?\s+([+-]?[\d]+,\d{2})\s+([+-]?[\d]+,\d{2})\s+([+-]?[\d]+,\d{2})\s+([+-]?[\d]+,\d{2})/i,
    },
    {
      label: "36m",
      re: /36\s*m[eê]s(?:es)?\s+([+-]?[\d]+,\d{2})\s+([+-]?[\d]+,\d{2})\s+([+-]?[\d]+,\d{2})\s+([+-]?[\d]+,\d{2})/i,
    },
    {
      label: "inicio",
      re: /desde\s+o\s+in[ií]cio\s+([+-]?[\d]+,\d{2})\s+([+-]?[\d]+,\d{2})\s+([+-]?[\d]+,\d{2})\s+([+-]?[\d]+,\d{2})/i,
    },
  ];
  let ret_12m = null,
    ret_cdi_12m = null,
    pct_cdi_12m = null,
    volat_12m = null;
  for (const def of PERIOD_DEFS) {
    const m = t.match(def.re);
    if (!m) continue;
    const portfolio = parsePct(m[1]);
    if (portfolio === null) continue;
    const cdi = parsePct(m[2]),
      pct_cdi = parsePct(m[3]),
      volat = parsePct(m[4]);
    periods.push({ label: def.label, portfolio, cdi, pct_cdi, volat });
    if (def.label === "12m") {
      ret_12m = portfolio;
      ret_cdi_12m = cdi;
      pct_cdi_12m = pct_cdi;
      volat_12m = volat;
    }
  }
  if (ret_12m === null) {
    const fb = t.match(/12\s*m[eê]s(?:es)?[^\n]{0,120}?([+-]?[\d]+,\d{2})(?=[\s\n])/i);
    if (fb) {
      const v = parsePct(fb[1]);
      if (v !== null && v > -50 && v < 200) ret_12m = v;
    }
  }

  // ── 5. Benchmarks ─────────────────────────────────────────────
  const benchmarks = {};
  const cdiMB = t.match(/\bCDI\b\s+([+-]?[\d]+,\d{2})\s*%/i);
  if (cdiMB) benchmarks.cdi_mes = parsePct(cdiMB[1]);
  const ibovMB = t.match(/(?:Ibovespa|IBOV)\s+([+-]?[\d]+,\d{2})\s*%/i);
  if (ibovMB) benchmarks.ibov_mes = parsePct(ibovMB[1]);
  const ipcaMB = t.match(/\bIPCA\b\s+([+-]?[\d]+,\d{2})\s*%/i);
  if (ipcaMB) benchmarks.ipca_mes = parsePct(ipcaMB[1]);

  // ── 6. Alocação ───────────────────────────────────────────────
  const allocation = [];
  const ALLOC_DEFS = [
    { name: "Renda Variável", re: /renda\s+vari[aá]vel[^%\n]{0,80}([\d]+,\d{2})\s*%/i },
    { name: "Título Público", re: /t[íi]tulo\s+p[úu]blico[^%\n]{0,80}([\d]+,\d{2})\s*%/i },
    { name: "Multimercado", re: /multimercado[^%\n]{0,80}([\d]+,\d{2})\s*%/i },
    { name: "Global", re: /\bglobal\b[^%\n]{0,80}([\d]+,\d{2})\s*%/i },
    { name: "Pós-fixado", re: /p[oó]s.?fixado[^%\n]{0,80}([\d]+,\d{2})\s*%/i },
    { name: "Pré-fixado", re: /pr[eé].?fixado[^%\n]{0,80}([\d]+,\d{2})\s*%/i },
    { name: "Renda Fixa", re: /renda\s+fixa[^%\n]{0,80}([\d]+,\d{2})\s*%/i },
    { name: "Previdência", re: /previd[eê]ncia[^%\n]{0,80}([\d]+,\d{2})\s*%/i },
  ];
  for (const def of ALLOC_DEFS) {
    const am = t.match(def.re);
    if (am) {
      const pct = parsePct(am[1]);
      if (pct !== null && pct > 0 && pct <= 100) allocation.push({ name: def.name, pct });
    }
  }

  // ── 7. Histórico mensal (pg.4) ────────────────────────────────
  // "2025 3,95  4,04  1,30 ... 18,33 10,82" (duplo-espaço = negativo)
  // "% do CDI 389,45 - - ..."
  // Posições [0..11]=Jan–Dez, [12]=Ano, [13]=Desde
  const monthly = [];
  let ret_2025 = null;
  let ret_2024 = null;
  let pct_cdi_month = null;
  const refYear = ref_date ? parseInt(ref_date.slice(0, 4), 10) : new Date().getFullYear();

  const parseNumLine = (suffix) => {
    const vals = [];
    const re = /( *)([\d]{1,3}(?:\.[\d]{3})*,[\d]{2}|-)/g;
    let m;
    while ((m = re.exec(suffix)) !== null) {
      if (m[2] === "-") {
        vals.push(null);
        continue;
      }
      const n = parseFloat(m[2].replace(/\./g, "").replace(",", "."));
      const isNeg = m[1].length >= 2;
      vals.push(isNeg ? -n : n);
    }
    return vals;
  };

  const textLines = t.split("\n");
  let lastYearIdx = -1;
  let lastYearVal = null;
  for (let li = 0; li < textLines.length; li++) {
    const line = textLines[li];
    const yearM = line.match(/^(20\d{2})\s/);
    if (yearM) {
      const yr = parseInt(yearM[1], 10);
      const suffix = line.slice(yearM[1].length);
      const vals = parseNumLine(suffix);
      if (vals.length >= 2) {
        for (let mi = 0; mi < 12 && mi < vals.length; mi++) {
          if (vals[mi] !== null)
            monthly.push({ month: MONTHS_PT[mi], year: yr, pct: vals[mi], pct_cdi: null });
        }
        if (vals[12] !== null) {
          if (yr === 2025) ret_2025 = vals[12];
          else if (yr === 2024) ret_2024 = vals[12];
        }
        lastYearVal = yr;
        lastYearIdx = li;
      }
    }
    if (
      lastYearIdx >= 0 &&
      li > lastYearIdx &&
      li <= lastYearIdx + 3 &&
      line.includes("% do CDI")
    ) {
      const pctSuffix = line.slice(line.indexOf("% do CDI") + 8);
      const pctVals = parseNumLine(pctSuffix);
      for (let mi = 0; mi < 12 && mi < pctVals.length; mi++) {
        if (pctVals[mi] === null) continue;
        for (let k = monthly.length - 1; k >= 0; k--) {
          if (monthly[k].year === lastYearVal && monthly[k].month === MONTHS_PT[mi]) {
            monthly[k].pct_cdi = pctVals[mi];
            break;
          }
        }
      }
      if (lastYearVal === refYear && pctVals[0] !== null) pct_cdi_month = pctVals[0];
      lastYearIdx = -1;
    }
  }

  // ── 8. Liquidez ───────────────────────────────────────────────
  // Labels reais: "0 a 1", "2 a 5", "16 a 30"
  const liquidez = {};
  const liq01 = t.match(/0\s+a\s+1[^\n]{0,80}?([\d]{1,3}(?:\.[\d]{3})+,\d{2})/i);
  if (liq01) liquidez.d0_d1 = parseBRL(liq01[1]);
  const liq25 = t.match(/2\s+a\s+5[^\n]{0,80}?([\d]{1,3}(?:\.[\d]{3})+,\d{2})/i);
  if (liq25) liquidez.d2_d5 = parseBRL(liq25[1]);
  const liq30 = t.match(/1[0-9]\s+a\s+30[^\n]{0,80}?([\d]{1,3}(?:\.[\d]{3})+,\d{2})/i);
  if (liq30) liquidez.d16_d30 = parseBRL(liq30[1]);
  // Aliases legados
  if (liquidez.d0_d1) liquidez.d0 = liquidez.d0_d1;
  if (liquidez.d2_d5) liquidez.d1_d30 = liquidez.d2_d5;
  if (liquidez.d16_d30) liquidez.d31_plus = liquidez.d16_d30;

  // ── 9. Posições detalhadas ────────────────────────────────────
  // Sem "%" nas colunas pct; negativo = duplo-espaço antes do número
  const assets_inter = [];
  const seenNames = new Set();
  const SKIP_SET = new Set([
    "CDI",
    "IBOV",
    "IBRA",
    "IBX",
    "IPCA",
    "SELIC",
    "IGP",
    "BRL",
    "USD",
    "ETF",
    "FII",
    "LCI",
    "LCA",
    "CRI",
    "CRA",
    "TOTAL",
    "ATIVO",
    "ATIVOS",
    "SALDO",
  ]);
  const ESTRATEGIAS = [
    "Título Público",
    "Pós-fixado",
    "Multimercado",
    "Renda Variável",
    "Global",
    "Pré-fixado",
    "Renda Fixa",
    "Previdência",
  ];
  const ESTRATEGIA_NORM = ESTRATEGIAS.map(normStr);
  let currentEstrat = null;

  const extractNums = (suffix) => {
    const vals = [];
    const re = /( *)([\d]{1,3}(?:\.[\d]{3})*,[\d]{2})/g;
    let m;
    while ((m = re.exec(suffix)) !== null) {
      const n = parseFloat(m[2].replace(/\./g, "").replace(",", "."));
      const isNeg = m[1].length >= 2;
      vals.push(isNeg ? -n : n);
    }
    return vals;
  };

  for (const line of textLines) {
    const lineN = normStr(line);
    let foundEstrat = false;
    for (let ei = 0; ei < ESTRATEGIAS.length; ei++) {
      if (
        lineN.includes(ESTRATEGIA_NORM[ei]) &&
        line.length < 60 &&
        !lineN.includes("saldo") &&
        !lineN.includes("%")
      ) {
        currentEstrat = ESTRATEGIAS[ei];
        foundEstrat = true;
        break;
      }
    }
    if (foundEstrat || !currentEstrat) continue;
    if (
      lineN.includes("saldo anterior") ||
      lineN.includes("rentabilidade") ||
      lineN.includes("aplicacoes") ||
      lineN.includes("participacao") ||
      lineN.includes("meses acima") ||
      lineN.includes("meses abaixo") ||
      lineN.includes("benchmark") ||
      lineN.includes("% do cdi") ||
      lineN.startsWith("total")
    )
      continue;

    const brlRe = /([\d]{1,3}(?:\.[\d]{3})*,[\d]{2})/g;
    const brlMatches = [...line.matchAll(brlRe)];
    if (brlMatches.length < 5) continue;

    const firstBrlIdx = brlMatches[0].index;
    const namePart = line.substring(0, firstBrlIdx).trim();
    if (!namePart || namePart.length < 2) continue;
    const nameN = normStr(namePart);
    if (nameN.startsWith("total")) continue;
    // Rejeita linhas que claramente são linhas de estatística e não de ativo
    if (/^meses|^% do|^[0-9]{4}$|benchmark|% acima|% abaixo/i.test(namePart)) continue;

    const nameNorm = normStr(namePart);
    if (seenNames.has(nameNorm)) continue;
    if ([...SKIP_SET].some((sk) => nameNorm === normStr(sk))) continue;

    // Filtrar sub-totais e linhas resumo que duplicam valores
    if (nameNorm.includes("sub") && (nameNorm.includes("total") || nameNorm.includes("- total")))
      continue;
    // Filtrar linhas de cabeçalho de estratégia com valores (são resumos, não posições)
    if (ESTRATEGIA_NORM.some((en) => nameNorm === en)) continue;
    // Filtrar anos soltos (2021, 2024, etc.) e "Liquidez"
    if (/^20\d{2}\s*-?\s*$/.test(namePart.trim())) continue;
    if (nameNorm === "liquidez" || nameNorm === "pos - fixado" || nameNorm === "pos-fixado")
      continue;

    const tickerMatch = namePart.match(/\b([A-Z]{3,5}[0-9]{1,2})\b/);
    const ticker = tickerMatch ? tickerMatch[1] : namePart.slice(0, 40).trim();
    if (SKIP_SET.has(ticker)) continue;

    seenNames.add(nameNorm);
    const nums = extractNums(line.slice(firstBrlIdx));
    const brlVals = nums.slice(0, 5);
    const pctVals = nums.slice(5, 9);

    assets_inter.push({
      ticker,
      nome: sanitizeStr(namePart),
      estrategia: currentEstrat,
      saldo_anterior: brlVals[0] ?? null,
      aplicacoes: brlVals[1] ?? null,
      resgates: brlVals[2] ?? null,
      eventos: brlVals[3] ?? null,
      saldo_bruto: brlVals[4] ?? null,
      rent_mes: pctVals[0] ?? null,
      rent_12m: pctVals[1] ?? null,
      rent_inception: pctVals[2] ?? null,
      participacao: pctVals[3] ?? null,
    });
  }

  // Fallback ticker BR
  if (assets_inter.length === 0) {
    const brPosRe =
      /\b([A-Z]{3,5}[0-9]{1,2})\b[^\n]{0,200}?([\d]{1,3}(?:\.[\d]{3})+,[\d]{2})(?:[^\n]{0,80}?([+-]?[\d]+,[\d]{2})(?!\s*%))?/g;
    const seenFb = new Set();
    let bm;
    while ((bm = brPosRe.exec(t)) !== null) {
      const ticker = bm[1];
      if (seenFb.has(ticker) || SKIP_SET.has(ticker)) continue;
      const value = parseBRL(bm[2]);
      if (!value || value < 500) continue;
      const retPct = bm[3] != null ? parsePct(bm[3]) : null;
      seenFb.add(ticker);
      assets_inter.push({
        ticker,
        nome: ticker,
        estrategia: "Outros",
        saldo_bruto: value,
        rent_mes: retPct !== null && retPct > -50 && retPct < 100 ? retPct : null,
      });
    }
  }

  // ── 10. Movimentações ─────────────────────────────────────────
  const movimentacoes = [];
  const movRe = /(\d{1,2}\/\d{1,2}\/\d{4})[^\n]{3,80}?([\d]{1,3}(?:\.[\d]{3})+,\d{2})/g;
  let movM;
  while ((movM = movRe.exec(t)) !== null) {
    const valor = parseBRL(movM[2]);
    const tipoRaw = movM[0]
      .slice(movM[1].length, movM[0].length - movM[2].length)
      .replace(/[R$\d.,\s]/g, "")
      .trim()
      .slice(0, 40);
    if (valor && valor > 0 && tipoRaw.length > 1)
      movimentacoes.push({ data: movM[1], tipo: tipoRaw, valor });
  }

  // ── 11. Warnings ──────────────────────────────────────────────
  const warnings = [];
  if (!patrimonio_total) warnings.push("Patrimônio não identificado — verifique o PDF.");
  if (!ret_month) warnings.push("Rentabilidade do mês não identificada.");
  if (assets_inter.length === 0)
    warnings.push("Posições detalhadas não extraídas — tabela não reconhecida.");

  return {
    source: "interwin_pdf",
    ref_date,
    period,
    patrimonio_total,
    patrimonio_inter: patrimonio_total,
    ret_month,
    ret_year,
    ret_12m,
    ret_inception,
    acumulado: ret_inception,
    ret_2025,
    ret_2024,
    ret_cdi_12m,
    pct_cdi_12m,
    volat_12m,
    pct_cdi_month,
    aplicacoes_mes,
    resgates_mes,
    eventos_mes,
    ganhos_mes,
    ganho_month: ganhos_mes,
    ganho_12m,
    benchmarks: Object.keys(benchmarks).length > 0 ? benchmarks : null,
    periods: periods.length > 0 ? periods : null,
    allocation: allocation.length > 0 ? allocation : null,
    assets_inter: assets_inter.length > 0 ? assets_inter : null,
    liquidez: Object.keys(liquidez).length > 0 ? liquidez : null,
    movimentacoes: movimentacoes.length > 0 ? movimentacoes : null,
    monthly: monthly.length > 0 ? monthly : null,
    warnings,
  };
}

// ─── PDF: extrai dados financeiros do texto ───────────────────────

function parsePDFText(rawText, filename) {
  const t = rawText;
  const tn = normStr(rawText);
  const parseBRL = (s) => {
    const n = parseFloat(s.replace(/\./g, "").replace(",", "."));
    return isNaN(n) || n <= 0 ? null : n;
  };

  // Padrões de total/saldo em ordem de prioridade
  const PATTERNS = [
    /(?:total|saldo)\s+(?:da\s+)?(?:carteira|patrimonio|posicao|investimentos|aplicacoes)[^R\n]{0,120}([\d]{1,3}(?:\.\d{3})+,\d{2})/i,
    /patrimonio\s+(?:total|liquido|bruto)[^R\n]{0,80}([\d]{1,3}(?:\.\d{3})+,\d{2})/i,
    /posicao\s+consolidada[^R\n]{0,80}([\d]{1,3}(?:\.\d{3})+,\d{2})/i,
    /total\s+investido[^R\n]{0,80}([\d]{1,3}(?:\.\d{3})+,\d{2})/i,
    /total\s+(?:bruto|liquido)[^R\n]{0,80}([\d]{1,3}(?:\.\d{3})+,\d{2})/i,
    /valor\s+(?:bruto|liquido)\s+total[^R\n]{0,80}([\d]{1,3}(?:\.\d{3})+,\d{2})/i,
    // fallback: "Total" seguido de valor com pelo menos 4 dígitos antes da vírgula
    /\btotal\b[^\n]{0,60}([\d]{1,3}(?:\.\d{3}){1,},\d{2})/i,
  ];

  let patrimonio_total = null;
  for (const re of PATTERNS) {
    const m = t.match(re);
    if (m) {
      const v = parseBRL(m[1]);
      if (v && v > 500) {
        patrimonio_total = v;
        break;
      }
    }
  }

  // Retorno mensal
  let ret_month = null;
  const retRe =
    /(?:retorno|rentabilidade|rendimento)\s+(?:do\s+)?(?:mes|mês|mensal)[^\n]{0,80}([+-]?\d{1,3},\d{2})\s*%/i;
  const retM = t.match(retRe);
  let pct_cdi_month = null;
  let pct_cdi_12m = null;
  if (retM) {
    const v = parseFloat(retM[1].replace(",", "."));
    if (!isNaN(v) && v > -30 && v < 30) ret_month = v;

    // %CDI extraction (for XP and other brokers)
    const pctCDIPatterns = [
      /% ?CDI\s+(?:m[eê]s|mensal)[^\n]{0,80}([+-]?[\d]+,\d{2})\s*%/i,
      /(?:rentabilidade|retorno)\s+vs\s+CDI[^\n]{0,80}([+-]?[\d]+,\d{2})\s*%/i,
      /CDI\s+(?:m[eê]s|mensal)[^\n]{0,80}([+-]?[\d]+,\d{2})\s*%/i,
    ];

    for (const pattern of pctCDIPatterns) {
      const m = t.match(pattern);
      if (m) {
        const v = parseFloat(m[1].replace(",", "."));
        if (!isNaN(v) && v > 50 && v < 300) {
          pct_cdi_month = v;
          break;
        }
      }
    }

    const pctCDI12mPatterns = [
      /% ?CDI\s+(?:12\s*m[eê]s|12m|doze\s+meses)[^\n]{0,80}([+-]?[\d]+,\d{2})\s*%/i,
      /(?:12\s*m[eê]s|12m)[^\n]{0,80}CDI[^\n]{0,80}([+-]?[\d]+,\d{2})\s*%/i,
      /CDI\s+(?:12\s*m[eê]s|12m)[^\n]{0,80}([+-]?[\d]+,\d{2})\s*%/i,
    ];

    for (const pattern of pctCDI12mPatterns) {
      const m = t.match(pattern);
      if (m) {
        const v = parseFloat(m[1].replace(",", "."));
        if (!isNaN(v) && v > 50 && v < 300) {
          pct_cdi_12m = v;
          break;
        }
      }
    }
  }

  // Corretora — pelo nome de arquivo ou conteúdo
  let broker = detectBrokerFromFilename(filename);
  if (broker === "unknown") {
    if (tn.includes("xp investimentos")) broker = "xp";
    else if (tn.includes("btg pactual")) broker = "btg";
    else if (tn.includes("bradesco")) broker = "bradesco";
    else if (tn.includes("banco itau") || tn.includes("itauunibanco")) broker = "itau";
    else if (tn.includes("santander")) broker = "santander";
    else if (tn.includes("interwin") || tn.includes("relatorio de rentabilidade"))
      broker = "interwin";
    else if (tn.includes("banco inter")) broker = "inter";
    else if (tn.includes("nubank")) broker = "nubank";
    else if (tn.includes("avenue") || tn.includes("account value")) broker = "avenue";
  }

  const SOURCE = {
    xp: "xp_pdf",
    btg: "btg_pdf",
    bradesco: "bradesco_pdf",
    itau: "itau_pdf",
    santander: "santander_pdf",
    inter: "inter_pdf",
    interwin: "interwin_pdf",
    avenue: "avenue_pdf",
    nubank: "nubank_pdf",
    unknown: "pdf_generico",
  };

  return {
    result: {
      source: SOURCE[broker] || "pdf_generico",
      patrimonio_total,
      ret_month,
      pct_cdi_month,
      pct_cdi_12m,
      ...(broker === "xp" ? { patrimonio_xp: patrimonio_total } : {}),
      ...(broker === "btg" ? { patrimonio_btg: patrimonio_total } : {}),
    },
    broker,
  };
}

// ─── Parser XP (XLSX) ────────────────────────────────────────────

function parseXP(workbook, XLSX) {
  const result = {
    source: "xp_xlsx",
    assets_xp: { posfix: [], inflacao: [], prefixado: [], prev: [] },
    patrimonio_xp: null,
    patrimonio_total: null,
    pct_cdi_month: null,
    pct_cdi_12m: null,
    movimentacoes: [],
  };

  const posSheet = workbook.SheetNames.find(
    (n) =>
      normStr(n).includes("posicao") ||
      normStr(n).includes("posição") ||
      normStr(n).includes("ativos") ||
      normStr(n).includes("carteira") ||
      n === workbook.SheetNames[0]
  );
  if (!posSheet) return { error: "Sheet de posição não encontrada.", source: "xp_xlsx" };

  const ws = workbook.Sheets[posSheet];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  let headerRow = -1;
  for (let i = 0; i < Math.min(rows.length, 20); i++) {
    const r = rows[i].map(normStr);
    if (r.some((c) => c.includes("nome") || c.includes("ativo") || c.includes("papel"))) {
      headerRow = i;
      break;
    }
  }
  if (headerRow === -1) return { error: "Cabeçalho não encontrado.", source: "xp_xlsx" };

  const h = rows[headerRow];
  const iNome = colIdx(h, ["nome", "ativo", "papel", "descricao"]);
  const iTipo = colIdx(h, ["tipo", "classe", "categoria", "produto"]);
  const iTaxa = colIdx(h, ["taxa", "rentabilidade", "indexador"]);
  const iValor = colIdx(h, ["valor bruto", "valor atual", "valor de mercado", "total"]);
  const iLiq = colIdx(h, ["valor liquido", "valor liq", "liquido"]);
  const iPct = colIdx(h, ["participacao", "perc", "%", "peso", "percentual"]);
  const iVenc = colIdx(h, ["vencimento", "data venc", "prazo"]);
  const iRating = colIdx(h, ["rating", "classificacao"]);

  let totalXP = 0;
  for (let i = headerRow + 1; i < rows.length; i++) {
    const r = rows[i];
    const nome = String(r[iNome] || "").trim();
    if (!nome) continue;
    if (normStr(nome).includes("total") || normStr(nome).includes("subtotal")) {
      const v = toNum(r[iValor]);
      if (v && v > 1000) totalXP = Math.max(totalXP, v);
      continue;
    }
    const asset = {
      n: nome,
      tipo: String(r[iTipo] || "").trim() || "Renda Fixa",
      taxa: String(r[iTaxa] || "").trim() || "–",
      v: toNum(r[iValor]) || 0,
      liq: toNum(r[iLiq]) || toNum(r[iValor]) || 0,
      pct: toNum(r[iPct]) || 0,
      venc: String(r[iVenc] || "").trim() || "–",
      rat: String(r[iRating] || "").trim() || "–",
      br: "XP",
    };
    if (asset.v <= 0) continue;
    totalXP += asset.v;
    const t = normStr(asset.tipo + " " + asset.taxa + " " + asset.n);
    if (t.includes("prev") || t.includes("vgbl") || t.includes("pgbl"))
      result.assets_xp.prev.push(asset);
    else if (t.includes("ipca") || t.includes("ntn-b") || t.includes("inflacao"))
      result.assets_xp.inflacao.push(asset);
    else if (t.includes("pre") || t.includes("prefixado") || t.includes("ltn"))
      result.assets_xp.prefixado.push(asset);
    else result.assets_xp.posfix.push(asset);
  }
  if (totalXP > 0) result.patrimonio_xp = totalXP;

  const perfSheet = workbook.SheetNames.find(
    (n) =>
      normStr(n).includes("performance") ||
      normStr(n).includes("rentabilidade") ||
      normStr(n).includes("retorno")
  );
  if (perfSheet) {
    const prows = XLSX.utils.sheet_to_json(workbook.Sheets[perfSheet], { header: 1, defval: "" });
    for (const row of prows) {
      const flat = row.map(normStr);
      if (flat.some((c) => c.includes("mes") || c.includes("mês") || c.includes("mensal"))) {
        for (let j = 0; j < row.length; j++) {
          const v = toNum(row[j]);
          if (v !== null && v > -10 && v < 30) {
            result.ret_month = result.ret_month ?? v;
            break;
          }
        }
      }
      if (flat.some((c) => c.includes("12 meses") || c.includes("12m"))) {
        for (let j = 0; j < row.length; j++) {
          const v = toNum(row[j]);
          if (v !== null && v > -30 && v < 100) {
            result.ret_12m = result.ret_12m ?? v;
            break;
          }
        }
      }
      // Extract %CDI columns (monthly and 12m)
      if (
        flat.some(
          (c) => c.includes("% cdi") || c.includes("%cdi") || (c.includes("cdi") && c.includes("%"))
        )
      ) {
        for (let j = 0; j < row.length; j++) {
          const v = toNum(row[j]);
          if (v !== null && v > 50 && v < 300) {
            if (!result.pct_cdi_month) result.pct_cdi_month = v;
            break;
          }
        }
      }
      if (
        flat.some(
          (c) =>
            c.includes("12m") &&
            (c.includes("% cdi") || c.includes("%cdi") || (c.includes("cdi") && c.includes("%")))
        )
      ) {
        for (let j = 0; j < row.length; j++) {
          const v = toNum(row[j]);
          if (v !== null && v > 50 && v < 300) {
            result.pct_cdi_12m = result.pct_cdi_12m ?? v;
            break;
          }
        }
      }
    }
  }

  // Extract movimentacoes (transactions/movements) from dedicated sheet
  const movSheet = workbook.SheetNames.find(
    (n) =>
      normStr(n).includes("movimentacoes") ||
      normStr(n).includes("movimentações") ||
      normStr(n).includes("operacoes") ||
      normStr(n).includes("operações") ||
      normStr(n).includes("transacoes") ||
      normStr(n).includes("extrato")
  );
  if (movSheet) {
    const mrows = XLSX.utils.sheet_to_json(workbook.Sheets[movSheet], { header: 1, defval: "" });
    let movHeaderRow = -1;
    for (let i = 0; i < Math.min(mrows.length, 10); i++) {
      const r = mrows[i].map(normStr);
      if (r.some((c) => c.includes("data") || c.includes("tipo") || c.includes("ativo"))) {
        movHeaderRow = i;
        break;
      }
    }
    if (movHeaderRow >= 0) {
      const mh = mrows[movHeaderRow];
      const mData = colIdx(mh, ["data", "date"]);
      const mTipo = colIdx(mh, ["tipo", "operation", "operacao"]);
      const mAtivo = colIdx(mh, ["ativo", "papel", "asset", "ticker"]);
      const mValor = colIdx(mh, ["valor", "value", "preco", "preço"]);
      const mQtd = colIdx(mh, ["quantidade", "qty", "qtd", "shares"]);
      const mNotas = colIdx(mh, ["notas", "notes", "descricao"]);
      for (let i = movHeaderRow + 1; i < mrows.length; i++) {
        const r = mrows[i];
        const data = String(r[mData] || "").trim();
        if (!data || normStr(data).includes("total")) continue;
        const mov = {
          data,
          tipo: String(r[mTipo] || "").trim(),
          ativo: String(r[mAtivo] || "").trim(),
          valor: toNum(r[mValor]),
          quantidade: toNum(r[mQtd]),
          notas: String(r[mNotas] || "").trim() || undefined,
        };
        if (mov.valor !== null && mov.valor > 0 && mov.tipo) {
          result.movimentacoes.push(mov);
        }
      }
    }
  }

  return result;
}

// ─── Parser BTG (XLSX) ───────────────────────────────────────────

function parseBTG(workbook, XLSX) {
  const result = { source: "btg_xlsx", assets_btg: [], patrimonio_btg: null };
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

  let headerRow = -1;
  for (let i = 0; i < Math.min(rows.length, 20); i++) {
    const r = rows[i].map(normStr);
    if (r.some((c) => c.includes("cod") || c.includes("emiss") || c.includes("vencim"))) {
      headerRow = i;
      break;
    }
  }
  if (headerRow === -1) return { error: "Cabeçalho BTG não encontrado.", source: "btg_xlsx" };

  const h = rows[headerRow];
  const iCode = colIdx(h, ["codigo", "code", "papel", "ativo"]);
  const iEm = colIdx(h, ["emissao", "emissão", "aplicacao", "data compra"]);
  const iVc = colIdx(h, ["vencimento", "data venc", "prazo"]);
  const iOrig = colIdx(h, ["valor aplicado", "original", "custo", "principal"]);
  const iBruto = colIdx(h, ["valor bruto", "bruto", "gross"]);
  const iLiq = colIdx(h, ["valor liquido", "liquido", "net"]);

  let totalBTG = 0;
  for (let i = headerRow + 1; i < rows.length; i++) {
    const r = rows[i];
    const code = String(r[iCode] || "").trim();
    if (!code) continue;
    const bruto = toNum(r[iBruto]) || toNum(r[iLiq]) || 0;
    if (bruto <= 0) continue;
    result.assets_btg.push({
      code,
      em: String(r[iEm] || "").trim() || "–",
      vc: String(r[iVc] || "").trim() || "–",
      orig: toNum(r[iOrig]) || bruto,
      bruto,
      liq: toNum(r[iLiq]) || bruto,
    });
    totalBTG += bruto;
  }
  if (totalBTG > 0) result.patrimonio_btg = totalBTG;
  return result;
}

const detectBrokerFromWorkbook = (wb) => {
  const s = wb.SheetNames.map(normStr);
  if (s.some((n) => n.includes("xp") || n.includes("xperformance"))) return "xp";
  if (s.some((n) => n.includes("btg") || n.includes("custodia") || n.includes("extrato")))
    return "btg";
  return "unknown";
};

// ─── Informe de Rendimentos (IR) — XP Investimentos / XP Vida ────
// Formato: PDF "INFORME DE RENDIMENTOS FINANCEIROS" ou
//          "INFORME DE RENDIMENTOS DE PLANOS DE PREVIDÊNCIA COMPLEMENTAR"
// Extrai: seções consolidadas + itens detalhados por ativo
// Retorna: { type: 'informe_ir', report_type, broker, ano_referencia, ... }

function parseInformeXP(rawText) {
  const t = rawText;

  // ── Detecta se é um Informe de Rendimentos ───────────────────
  const isInformeInvest =
    /informe\s+de\s+rendimentos\s+financeiros/i.test(t) ||
    /rendimentos\s+pagos\s+e\s+creditados/i.test(t);
  const isInformePrev =
    /informe\s+de\s+rendimentos\s+de\s+planos?\s+de\s+previd/i.test(t) ||
    /xp\s+vida\s+e\s+previd/i.test(t);

  if (!isInformeInvest && !isInformePrev) return null;

  const result = {
    type: "informe_ir",
    report_type: isInformePrev ? "previdencia" : "investimentos",
    broker: isInformePrev ? "xp_vida" : "xp",
    warnings: [],
  };

  // ── Ano-calendário / exercício ────────────────────────────────
  // Padrão investimentos: "ANO CALENDÁRIO DE 2025"
  // Padrão previdência:   "Imposto Pessoa Física - 2025"
  const anoCal = t.match(/ano[\s-]*calend[aá]rio\s*(?:de)?\s*:?\s*(\d{4})/i);
  result.ano_referencia = anoCal ? parseInt(anoCal[1], 10) : null;

  const anoEx = t.match(/exerc[ií]cio\s*(?:de)?\s*:?\s*(\d{4})/i);
  result.ano_exercicio = anoEx ? parseInt(anoEx[1], 10) : null;

  // Fallback: "Imposto Pessoa Física - 2025" ou "31/12/YYYY" ou "ano" perto de 20XX
  if (!result.ano_referencia) {
    const fallbacks = [
      /imposto\s+(?:de\s+)?renda?\s+pessoa\s+f[ií]sica\s*[-–:]\s*(\d{4})/i,
      /imposto\s+pessoa\s+f[ií]sica\s*[-–:]\s*(\d{4})/i,
      /31\/12\/(\d{4})/,
      /(?:ano|calend|refer)[^\n]{0,30}(20\d{2})/i,
    ];
    for (const re of fallbacks) {
      const m = t.match(re);
      if (m) {
        result.ano_referencia = parseInt(m[1], 10);
        break;
      }
    }
  }
  if (!result.ano_exercicio && result.ano_referencia) {
    result.ano_exercicio = result.ano_referencia + 1;
  }

  // ── Fonte pagadora ────────────────────────────────────────────
  const cnpjs = [...t.matchAll(/(\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2})/g)].map((m) => m[1]);
  result.fonte_cnpj = cnpjs[0] || null;

  // Nome da fonte — detecta pelo nome conhecido primeiro (mais confiável)
  if (/xp\s+investimentos\s+cc?t?vm/i.test(t)) {
    result.fonte_nome = "XP Investimentos CCTVM S/A";
  } else if (/xp\s+vida/i.test(t)) {
    result.fonte_nome = "XP Vida e Previdência S.A.";
  } else if (/banco\s+xp/i.test(t)) {
    result.fonte_nome = "Banco XP S/A";
  } else {
    // Fallback: tenta extrair de "Fonte Pagadora" (primeira linha com CNPJ)
    const fonteNome = t.match(
      /(?:fonte\s+pagadora|raz[aã]o\s+social)\s*[:\s]*\n?\s*([A-Z][^\n]{4,60}?)\s+\d{2}\.\d{3}/i
    );
    if (fonteNome) {
      result.fonte_nome = sanitizeStr(fonteNome[1].trim());
    }
  }

  // ── Helper: extrai valor BRL de texto ─────────────────────────
  const parseBRL = (s) => {
    if (!s || s === "–" || s === "-") return 0;
    const cleaned = String(s)
      .replace(/[R$\s]/g, "")
      .replace(/\./g, "")
      .replace(",", ".");
    const n = parseFloat(cleaned);
    return isNaN(n) ? 0 : n;
  };

  // ── Seção-based extraction ────────────────────────────────────
  // Real XP PDFs have sections ending with "Total:" or "Total " lines.
  // Values are plain numbers (no R$ prefix): "Total: 0,00 110.000,00 0,00"
  // Strategy: find each section header, then find its "Total:" line,
  // and extract the last numeric value(s) from that line.

  const lines = t.split("\n");

  // Find section boundaries by scanning lines
  // Common sections for both investimentos and previdência PDFs
  const sectionDefs = [
    {
      key: "rendimentos_isentos",
      re: /^RENDIMENTOS\s+ISENTOS/i,
      label: "Rendimentos Isentos e Não Tributáveis",
    },
    {
      key: "tributacao_exclusiva",
      re: /^RENDIMENTOS\s+SUJEITOS\s+[AÀ]\s+TRIBUTA/i,
      label: "Rendimentos Sujeitos à Tributação Exclusiva/Definitiva",
    },
    {
      key: "saldo_conta",
      re: /^SALDO\s+EM\s+CONTA/i,
      label: "Saldo em Conta",
    },
    {
      key: "creditos_transito",
      re: /^CR[EÉ]DITOS\s+EM\s+TR[AÂ]NSITO/i,
      label: "Créditos em Trânsito",
    },
    {
      key: "dividas_onus",
      re: /^D[IÍ]VIDAS\s+E\s+[OÔ]NUS/i,
      label: "Dívidas e Ônus Reais",
    },
    {
      key: "rendimentos_tributaveis",
      re: /^RENDIMENTOS\s+TRIBUT[AÁ]VEIS/i,
      label: "Rendimentos Tributáveis",
    },
    {
      key: "bens_direitos",
      re: /^Bens\s+e\s+Direitos/i,
      label: "Bens e Direitos",
    },
    {
      key: "contribuicoes_prev",
      re: /^Contribui[çc][oõ]es\s+efetuadas/i,
      label: "Contribuições para Previdência",
    },
  ];
  // Previdência-specific section patterns — only added for previdência PDFs
  // to avoid duplicate matches (these /i patterns also match uppercase investimentos headers)
  if (isInformePrev) {
    sectionDefs.push(
      {
        key: "rendimentos_isentos_prev",
        re: /^Rendimentos\s+isentos/i,
        label: "Rendimentos Isentos (Previdência)",
      },
      {
        key: "tributacao_exclusiva_prev",
        re: /^Rendimentos\s+sujeitos\s+[àa]/i,
        label: "Tributação Exclusiva (Previdência)",
      },
      {
        key: "rendimentos_tributaveis_prev",
        re: /^Rendimentos\s+tribut[áa]veis/i,
        label: "Rendimentos Tributáveis (Previdência)",
      }
    );
  }

  // Map: sectionKey → { startLine, endLine, totalLine, totalValues[] }
  const sectionRanges = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    for (const sd of sectionDefs) {
      if (sd.re.test(line)) {
        sectionRanges.push({ ...sd, startLine: i, endLine: null });
      }
    }
  }
  // Set endLine as start of next section (or end of text)
  for (let i = 0; i < sectionRanges.length; i++) {
    sectionRanges[i].endLine =
      i + 1 < sectionRanges.length ? sectionRanges[i + 1].startLine : lines.length;
  }

  // Extract "Total:" values from each section
  const extractTotalFromSection = (range) => {
    if (!range) return [];
    for (let i = range.startLine; i < range.endLine; i++) {
      const line = lines[i].trim();
      if (/^total\s*:?\s/i.test(line)) {
        // Extract all numeric values from the Total line
        const nums = [...line.matchAll(/([\d]+(?:\.[\d]{3})*,[\d]{2})/g)].map((m) =>
          parseBRL(m[1])
        );
        return nums;
      }
    }
    return [];
  };

  // ── Extract section totals ────────────────────────────────────
  // For investimentos PDFs: sections have columns [saldo_anterior, saldo_atual, rendimentos]
  // Total line: "Total: 0,00 110.000,00 0,00" → last value = rendimentos, middle = saldo_atual

  const findSection = (key) => sectionRanges.find((sr) => sr.key === key) || null;

  // Rendimentos Isentos
  let secRI = findSection("rendimentos_isentos");
  if (!secRI) secRI = findSection("rendimentos_isentos_prev");
  const riNums = extractTotalFromSection(secRI);
  // [saldo_anterior, saldo_atual, rendimentos] — saldo_atual is the important one for bens
  result.rendimentos_isentos =
    riNums.length >= 3 ? riNums[2] : riNums.length >= 1 ? riNums[riNums.length - 1] : 0;
  // Also store saldo for bens_direitos calculation
  const _riSaldo = riNums.length >= 2 ? riNums[1] : 0;

  // Tributação Exclusiva
  let secTE = findSection("tributacao_exclusiva");
  if (!secTE) secTE = findSection("tributacao_exclusiva_prev");
  const teNums = extractTotalFromSection(secTE);
  // For trib exclusiva: last value = rendimentos líquidos
  result.tributacao_exclusiva =
    teNums.length >= 3 ? teNums[2] : teNums.length >= 1 ? teNums[teNums.length - 1] : 0;
  const _teSaldo = teNums.length >= 2 ? teNums[1] : 0;

  // Saldo em Conta
  const secSC = findSection("saldo_conta");
  const scNums = extractTotalFromSection(secSC);
  result.saldo_conta = scNums.length >= 2 ? scNums[1] : scNums.length >= 1 ? scNums[0] : 0;

  // Créditos em Trânsito
  const secCT = findSection("creditos_transito");
  const ctNums = extractTotalFromSection(secCT);
  result.creditos_transito = ctNums.length >= 2 ? ctNums[1] : ctNums.length >= 1 ? ctNums[0] : 0;

  // Dívidas e Ônus
  const secDO = findSection("dividas_onus");
  const doNums = extractTotalFromSection(secDO);
  result.dividas_onus = doNums.length >= 2 ? doNums[1] : doNums.length >= 1 ? doNums[0] : 0;

  // Rendimentos Tributáveis
  let secRT = findSection("rendimentos_tributaveis");
  if (!secRT) secRT = findSection("rendimentos_tributaveis_prev");
  const rtNums = extractTotalFromSection(secRT);
  result.rendimentos_tributaveis =
    rtNums.length >= 2 ? rtNums[1] : rtNums.length >= 1 ? rtNums[0] : 0;

  // ── Previdência (XP Vida) ─────────────────────────────────────
  if (isInformePrev) {
    // Bens e Direitos — VGBL saldo
    const secBD = findSection("bens_direitos");
    const bdNums = extractTotalFromSection(secBD);
    result.saldo_vgbl = bdNums.length >= 2 ? bdNums[1] : bdNums.length >= 1 ? bdNums[0] : 0;

    // Also check for "Saldo nominal em VGBL" line directly
    if (!result.saldo_vgbl && secBD) {
      for (let i = secBD.startLine; i < secBD.endLine; i++) {
        const m = lines[i].match(
          /saldo\s+nominal\s+em\s+vgbl\s+([\d]+(?:\.[\d]{3})*,[\d]{2})\s+([\d]+(?:\.[\d]{3})*,[\d]{2})/i
        );
        if (m) {
          result.saldo_vgbl = parseBRL(m[2]); // saldo em 31/12/current
          break;
        }
      }
    }

    // Contribuições — PGBL
    const secCP = findSection("contribuicoes_prev");
    const cpNums = extractTotalFromSection(secCP);
    result.contribuicao_pgbl = cpNums.length >= 1 ? cpNums[cpNums.length - 1] : 0;

    // Also check for "Contribuição para PGBL" line directly
    if (!result.contribuicao_pgbl && secCP) {
      for (let i = secCP.startLine; i < secCP.endLine; i++) {
        const m = lines[i].match(
          /contribui[çc][aã]o\s+para\s+pgbl\s+([\d]+(?:\.[\d]{3})*,[\d]{2})/i
        );
        if (m) {
          result.contribuicao_pgbl = parseBRL(m[1]);
          break;
        }
      }
    }

    // Rendimentos de previdência (from trib exclusiva or tributáveis sections)
    result.rendimento_previdencia = 0;
    result.ir_retido_previdencia = 0;

    // Check rendimentos tributáveis for PGBL/VGBL lines with rendimento + IR
    if (secRT) {
      for (let i = secRT.startLine; i < secRT.endLine; i++) {
        const line = lines[i];
        if (/pgbl|vgbl/i.test(line)) {
          const nums = [...line.matchAll(/([\d]+(?:\.[\d]{3})*,[\d]{2})/g)].map((m) =>
            parseBRL(m[1])
          );
          if (nums.length >= 2) {
            result.rendimento_previdencia += nums[0];
            result.ir_retido_previdencia += nums[1];
          }
        }
      }
    }
  }

  // ── Itens detalhados ──────────────────────────────────────────
  // Real XP layout — items are on lines like:
  //   "BRB BANCO DE BRASILIA SA 0,00 110.000,00 0,00"
  //   "Vinland Ativo Advisory FIF CIC 13.952,85 0,00 939,67"
  //   "BANCO C6 S.A. 15.000,00 0,00 8.075,54"
  //   "Saldo nominal em VGBL 116.098,96 116.098,96"
  // Items have a name followed by 2-4 numeric values (no R$ prefix).
  // We scan within DETALHAMENTO sections (pages 3+) for investimentos,
  // or within each section for previdência.

  result.items = [];

  // Regex: line starting with a name (letters/digits) ending with 2+ BRL values
  // Uses [ \t] instead of \s to prevent matching across newlines
  // Includes \d in name class for names like "BANCO C6 S.A.", "MAG Cash"
  const itemRe =
    /^(?:\(Valores em Reais\)\s*)?([A-ZÀ-Ú][A-Za-zÀ-ú0-9 \t.,\-/()&]{3,70}?)\s+([\d]+(?:\.[\d]{3})*,[\d]{2})\s+([\d]+(?:\.[\d]{3})*,[\d]{2})(?:\s+([\d]+(?:\.[\d]{3})*,[\d]{2}))?/gm;

  // Determine where "DETALHAMENTO" starts (investimentos PDF pages 3+)
  const detalhamentoStart = t.search(/DETALHAMENTO\s+DOS\s+ATIVOS/i);

  // For investimentos: only scan from DETALHAMENTO section onward
  // For previdência: scan all sections
  const scanText = isInformePrev || detalhamentoStart < 0 ? t : t.substring(detalhamentoStart);
  const scanOffset = isInformePrev || detalhamentoStart < 0 ? 0 : detalhamentoStart;

  let im;
  while ((im = itemRe.exec(scanText)) !== null) {
    const name = im[1].trim();
    // Filter out headers, totals, page markers, and non-item lines
    if (/^total|^especifica|^declara|^ficha|^valores|^p[aá]gina|^cnpj|^irpf|^c[oó]d/i.test(name))
      continue;
    if (/^xp\s+investimentos|^banco\s+xp|^xp\s+vida/i.test(name)) continue;
    if (/saldos?\s+em\s+31/i.test(name)) continue;
    if (/rendimentos?\s+(l[ií]quidos?|isentos|tribut|sujeitos)/i.test(name)) continue;
    if (/tributa[çc][aã]o\s+exclusiva/i.test(name)) continue;
    if (/fonte\s+pagadora|pessoa\s+f[ií]sica|identifica/i.test(name)) continue;
    if (/informa[çc][oõ]es|autoatendimento|atendimento/i.test(name)) continue;
    if (/^fundos\s+de\s+investimento|^renda\s+fixa\b/i.test(name)) continue;

    const val1 = parseBRL(im[2]);
    const val2 = parseBRL(im[3]);
    const val3 = im[4] ? parseBRL(im[4]) : null;

    result.items.push({
      ativo_nome: sanitizeStr(name),
      cnpj_emissor: null,
      grupo_irpf: null,
      codigo_irpf: null,
      saldo_anterior: val1,
      saldo_atual: val2,
      rendimento: val3 !== null ? val3 : 0,
      ir_retido: 0,
      _textPos: scanOffset + im.index,
    });
  }

  // Try to attach CNPJ from the line below each item
  for (const item of result.items) {
    const afterItem = t.substring(item._textPos);
    const cnpjMatch = afterItem.match(/\n[^\n]{0,30}?(\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2})/);
    if (cnpjMatch && cnpjMatch.index < 200) {
      item.cnpj_emissor = cnpjMatch[1];
    }
    // Try to get grupo/codigo from "Grupo XX" / "Cód. XX" in nearby lines
    const nearbyText = afterItem.substring(0, 300);
    const grupoMatch = nearbyText.match(/grupo\s+(\d{2})/i);
    const codMatch = nearbyText.match(/c[oó]d\.?\s+(\d{2})/i);
    if (grupoMatch) item.grupo_irpf = grupoMatch[1];
    if (codMatch) item.codigo_irpf = codMatch[1];
  }

  // ── Seção dos itens (classifica por posição) ──────────────────
  // Use section headers from the DETALHAMENTO area
  const sectionHeaders = [
    { re: /RENDIMENTOS\s+ISENTOS/gi, name: "rendimentos_isentos" },
    {
      re: /RENDIMENTOS\s+SUJEITOS\s+[AÀ]\s*\n?\s*TRIBUTA[ÇC][AÃ]O\s+EXCLUSIVA/gi,
      name: "tributacao_exclusiva",
    },
    {
      re: /TRIBUTA[ÇC][AÃ]O\s+EXCLUSIVA/gi,
      name: "tributacao_exclusiva",
    },
    { re: /RENDIMENTOS\s+TRIBUT[AÁ]VEIS/gi, name: "rendimentos_tributaveis" },
    { re: /(?<!Ficha\s)BENS\s+E\s+DIREITOS/gi, name: "bens_direitos" },
    { re: /D[IÍ]VIDAS\s+E\s+[OÔ]NUS/gi, name: "dividas" },
    {
      re: /CONTRIBUI[ÇC][OÕ]ES\s+EFETUADAS/gi,
      name: "contribuicoes_previdencia",
    },
  ];

  // Collect ALL positions of section headers (some repeat for page 1 vs page 3+)
  const allHeaderPositions = [];
  for (const sh of sectionHeaders) {
    let m;
    while ((m = sh.re.exec(t)) !== null) {
      allHeaderPositions.push({ name: sh.name, pos: m.index });
    }
  }
  allHeaderPositions.sort((a, b) => a.pos - b.pos);

  // Assign section to each item based on nearest preceding header
  for (const item of result.items) {
    let bestSection = "outros";
    for (let i = allHeaderPositions.length - 1; i >= 0; i--) {
      if (item._textPos > allHeaderPositions[i].pos) {
        bestSection = allHeaderPositions[i].name;
        break;
      }
    }
    item.secao = bestSection;
    delete item._textPos; // Clean up internal field
  }

  // ── Totais por seção ──────────────────────────────────────────
  result.totals = [];
  if (result.rendimentos_isentos > 0) {
    result.totals.push({
      secao: "rendimentos_isentos",
      descricao: "Rendimentos Isentos e Não Tributáveis",
      valor_total: result.rendimentos_isentos,
    });
  }
  if (result.tributacao_exclusiva > 0) {
    result.totals.push({
      secao: "tributacao_exclusiva",
      descricao: "Rendimentos Sujeitos à Tributação Exclusiva/Definitiva",
      valor_total: result.tributacao_exclusiva,
    });
  }
  if (result.rendimentos_tributaveis > 0) {
    result.totals.push({
      secao: "rendimentos_tributaveis",
      descricao: "Rendimentos Tributáveis",
      valor_total: result.rendimentos_tributaveis,
    });
  }
  if (isInformePrev) {
    if (result.saldo_vgbl > 0) {
      result.totals.push({
        secao: "bens_direitos",
        descricao: "Saldo VGBL (Bens e Direitos)",
        valor_total: result.saldo_vgbl,
      });
    }
    if (result.contribuicao_pgbl > 0) {
      result.totals.push({
        secao: "contribuicoes_previdencia",
        descricao: "Contribuição PGBL",
        valor_total: result.contribuicao_pgbl,
      });
    }
  }

  // ── Validação / warnings ──────────────────────────────────────
  if (!result.ano_referencia) {
    result.warnings.push("Ano-calendário não identificado no informe.");
  }
  if (result.items.length === 0) {
    result.warnings.push(
      "Nenhum ativo detalhado extraído. O PDF pode ter um layout não reconhecido."
    );
  }
  if (
    !result.rendimentos_isentos &&
    !result.tributacao_exclusiva &&
    !result.rendimentos_tributaveis &&
    !result.saldo_vgbl &&
    !result.contribuicao_pgbl
  ) {
    result.warnings.push("Nenhum valor consolidado identificado no informe.");
  }

  return result;
}

// ─── Função principal exportada ───────────────────────────────────

/**
 * Faz parse de qualquer extrato (XLSX, CSV ou PDF).
 * @returns {Promise<{ok, broker, data, warnings?, error?, partial?}>}
 */
export async function parseExtrato(file, forceBroker) {
  // ── Validação de tipo MIME + extensão ───────────────────────
  const VALID_MIMES = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "text/csv",
    "application/csv",
    "application/x-ofx",
    "text/x-ofx",
    "text/plain", // OFX files sometimes detected as text/plain
  ];
  const VALID_EXTS = ["pdf", "xlsx", "xls", "csv", "ofx"];
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  if (!VALID_EXTS.includes(ext))
    return {
      ok: false,
      broker: "unknown",
      data: null,
      error: `Formato .${ext} não suportado. Use PDF, XLSX, CSV ou OFX.`,
    };
  if (file.type && !VALID_MIMES.includes(file.type))
    return {
      ok: false,
      broker: "unknown",
      data: null,
      error: `Tipo MIME inválido: ${file.type}. Esperado: PDF, Excel, CSV ou OFX.`,
    };
  if (file.size > 50 * 1024 * 1024)
    return { ok: false, broker: "unknown", data: null, error: "Arquivo muito grande (máx 50 MB)." };

  // ── PDF ──────────────────────────────────────────────────────────
  if (ext === "pdf") {
    try {
      const text = await extractPDFText(file);
      const broker = forceBroker || detectBrokerFromFilename(file.name);

      // ── Informe de Rendimentos (IR) — deve ser testado ANTES ──
      // dos parsers de extrato, pois o mesmo PDF pode conter "XP"
      // no nome mas ser um informe fiscal e não um extrato mensal.
      {
        const ir = parseInformeXP(text);
        if (ir) {
          const hasData =
            ir.rendimentos_isentos > 0 ||
            ir.tributacao_exclusiva > 0 ||
            ir.rendimentos_tributaveis > 0 ||
            ir.saldo_vgbl > 0 ||
            ir.contribuicao_pgbl > 0 ||
            ir.items.length > 0;
          return {
            ok: hasData,
            broker: ir.broker || "xp",
            data: ir,
            warnings: ir.warnings || [],
            ...(!hasData
              ? {
                  partial: true,
                  error:
                    "Informe de Rendimentos identificado mas sem dados extraídos. Verifique o PDF.",
                }
              : {}),
          };
        }
      }

      // ── Avenue Securities ─────────────────────────────────────
      if (broker === "avenue" || broker === "unknown") {
        const av = parseAvenuePDF(text);
        if (av) {
          const ok = !!av.patrimonio_usd;
          return {
            ok,
            broker: "avenue",
            data: av,
            warnings: av.warnings || [],
            ...(!ok
              ? { partial: true, error: av.warnings?.[0] || "Total Avenue não identificado." }
              : {}),
          };
        }
      }

      // ── Interwin / Banco Inter Rentabilidade ──────────────────
      if (broker === "interwin" || broker === "inter" || broker === "unknown") {
        const iw = parseInterwinPDF(text);
        if (iw) {
          const ok = !!iw.patrimonio_total;
          return {
            ok,
            broker: "interwin",
            data: iw,
            warnings: iw.warnings || [],
            ...(!ok
              ? {
                  partial: true,
                  error: iw.warnings?.[0] || "Patrimônio Interwin não identificado.",
                }
              : {}),
          };
        }
      }

      // ── Parser genérico (fallback) ────────────────────────────
      const { result, broker: detectedBroker } = parsePDFText(text, file.name);
      if (!result.patrimonio_total) {
        return {
          ok: false,
          broker: detectedBroker,
          partial: true,
          data: result,
          error:
            "Patrimônio não identificado no PDF. O arquivo foi salvo para análise manual pelo assessor.",
        };
      }
      const warnings = [];
      if (!result.ret_month)
        warnings.push("Retorno do mês não encontrado — preencha manualmente se necessário.");
      return { ok: true, broker: detectedBroker, data: result, warnings };
    } catch (err) {
      return {
        ok: false,
        broker: forceBroker || detectBrokerFromFilename(file.name),
        error: `Erro ao ler PDF: ${err.message}`,
      };
    }
  }

  // ── CSV — tentativa como Avenue antes de converter para XLSX ────
  if (ext === "csv") {
    try {
      const broker = forceBroker || detectBrokerFromFilename(file.name);
      if (broker === "avenue" || broker === "unknown") {
        const text = await file.text();
        const avCsv = parseAvenueCSV(text);
        if (avCsv) {
          const ok = !!avCsv.patrimonio_usd;
          return {
            ok,
            broker: "avenue",
            data: avCsv,
            warnings: avCsv.warnings || [],
            ...(!ok
              ? {
                  partial: true,
                  error: avCsv.warnings?.[0] || "Total Avenue (CSV) não identificado.",
                }
              : {}),
          };
        }
      }
      // CSV não-Avenue cai no handler XLSX abaixo (SheetJS lê CSV)
    } catch (_e) {
      // silencia — tenta via XLSX abaixo
    }
  }

  // ── XLSX / CSV ───────────────────────────────────────────────────
  // XLSX é importado de forma lazy aqui — só carregado quando o usuário
  // realmente enviar um arquivo .xlsx/.csv (~500 KB brutos, ~130 KB gzip).
  try {
    const XLSX = await import("xlsx");
    const buffer = await file.arrayBuffer();
    const wb = XLSX.read(buffer, { type: "array", cellDates: true });
    const broker =
      forceBroker || detectBrokerFromWorkbook(wb) || detectBrokerFromFilename(file.name);
    const parsed = broker === "btg" ? parseBTG(wb, XLSX) : parseXP(wb, XLSX);

    if (parsed.error) return { ok: false, broker, error: parsed.error };

    if (!parsed.patrimonio_total) {
      const xp = parsed.patrimonio_xp || 0,
        btg = parsed.patrimonio_btg || 0;
      if (xp + btg > 0) parsed.patrimonio_total = xp + btg;
    }
    const warnings = [];
    if (!parsed.ret_month) warnings.push("Retorno do mês não encontrado — preencha manualmente.");
    if (!parsed.patrimonio_total)
      warnings.push("Patrimônio total não identificado — confirme após importar.");
    return { ok: true, broker, data: parsed, warnings };
  } catch (err) {
    return { ok: false, broker: "unknown", error: `Erro ao ler arquivo: ${err.message}` };
  }
}
